import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { IUser } from '@core/models';
import { PcoDialogService } from '@core/services/pco-dialog.service';
import { setToggleSidebar } from '@core/store/actions';
import { getInitials, logoutExtras } from '@core/utils';
import { Store } from '@ngrx/store';
import { selectCoreShowSidebar } from '@store/selectors/core.selector';
import { selectUserState } from '@store/selectors/user.selector';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Observable } from 'rxjs';

/**
 * Array of posible header classes
 */
export type THeaderClasses = string[];

/**
 * Main Header
 *
 * Show information like logo, pages menus.
 * Props:
 *
 * **[headerClasses]:** Change the style

 */
@Component({
  standalone: true,
  imports: [CommonModule, MenuModule, ButtonModule],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  /**
   * Customize header:
   *
   * Classes available:
   * - ['no-bg-to-start']: remove bg color before to scroll
   * - ['no-height']: remove default top height
   * - ['hidde-action-buttons']: hidden action buttons
   *
   * Example:
   * [headerClasses]="['no-height', 'no-bg-to-start']"
   */
  @Input() headerClasses: THeaderClasses = [];

  /**
   * Enables/disables some elements depending on the section where they are being displayed.
   */
  @Input() hideElements: string[] = [];

  backgroundColor = 'transparent';

  user: IUser | null = null;

  items: MenuItem[] = [];

  showModalLogout = false;

  showSidebar!: boolean;

  getInitials = getInitials;

  validateFistLogin = false;

  showSidebar$: Observable<boolean>;

  constructor(
    private readonly store: Store,
    private readonly dialog: PcoDialogService,
    private readonly authService: MsalService
  ) {
    this.showSidebar$ = this.store.select(selectCoreShowSidebar);
  }

  ngOnInit() {
    this.store.select(selectUserState).subscribe((user: IUser) => {
      this.user = user;

      this.items = [
        {
          id: 'profile',
          label: 'Configuración',
          icon: 'ico ico-settings-01 icon-size-24',
          routerLink: '/perfil',
          disabled: false,
        },
        {
          id: 'logout',
          label: 'Cerrar sesión',
          icon: 'ico ico-log-in-02 icon-size-24',
          disabled: false,
          command: this.showDialog,
        },
      ];

      /**
       * This filter is used to avoid displaying elements when,
       * for example, you are in the process of sigin a service.
       */
      this.items = this.items.filter((item) => !this.hideElements.includes(item.id ?? ''));
    });

    window.scrollTo(0, 0);

    this.store.select(selectCoreShowSidebar).subscribe((value: boolean) => {
      this.showSidebar = value;
    });
  }

  ngOnDestroy() {
    this.headerClasses = [];
  }

  handleLogout = () => {
    this.authService.logoutPopup().subscribe({
      next: () => {
        logoutExtras();
        window.pcoLocation.href = '/';
      },
    });
  };

  toggleSidebar() {
    this.store.dispatch(setToggleSidebar());
  }

  showDialog = () => {
    this.dialog.show({
      type: 'warn',
      title: '¿Deseas cerrar sesión?',
      body: 'Cierra sesión para salir de la plataforma. Si deseas ingresar deberás iniciar sesión nuevamente.',
      buttons: [
        {
          label: 'Cancelar',
          btnClass: 'p-button-text p-button-tertiary',
        },
        {
          label: 'Cerrar sesión',
          cb: this.handleLogout,
        },
      ],
    });
  };

  /**
   * Check if hidde a element
   * @param element the element to compare if hide
   * @returns bolean
   */
  hide = (element: string): boolean => {
    return !this.hideElements.includes(element);
  };
}
